import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { dispatchService, Dispatch, CreateDispatchRequest, RequestOTPRequest, VerifyOTPRequest, ItemScanResult, UpdateDispatchRequest } from "../../services/dispatchService";

// Async thunks for dispatch operations

export const getAllDispatch = createAsyncThunk(
  'dispatch/getAllDispatch',
  async ( _, { rejectWithValue }) => {
    try {
      const response = await dispatchService.getAllDispatch();
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.error?.message || 'Failed to get all dispatches');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to get all dispatches');
    }
  }
);

export const createDispatch = createAsyncThunk(
  'dispatch/createDispatch',
  async (payload: CreateDispatchRequest, { rejectWithValue }) => {
    try {
      const response = await dispatchService.createDispatch(payload);
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.error?.message || 'Failed to create dispatch');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create dispatch');
    }
  }
);

export const updateDispatch = createAsyncThunk(
  'dispatch/updateDispatch',
  async ({ id, payload }: { id: string; payload: UpdateDispatchRequest }, { rejectWithValue }) => {
    try {
      const response = await dispatchService.updateDispatch(id, payload);
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.error?.message || 'Failed to update dispatch');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update dispatch');
    }
  }
);

export const getDispatchDetails = createAsyncThunk(
  'dispatch/getDispatchDetails',
  async (dispatchId: string, { rejectWithValue }) => {
    try {
      const response = await dispatchService.getDispatchDetails(dispatchId);
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.error?.message || 'Failed to get dispatch details');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to get dispatch details');
    }
  }
);

export const getDriverDispatches = createAsyncThunk(
  'dispatch/getDriverDispatches',
  async (driverId: string, { rejectWithValue }) => {
    try {
      const response = await dispatchService.getDriverDispatches(driverId);
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.error?.message || 'Failed to get driver dispatches');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to get driver dispatches');
    }
  }
);

export const requestOTP = createAsyncThunk(
  'dispatch/requestOTP',
  async (payload: RequestOTPRequest, { rejectWithValue }) => {
    try {
      const response = await dispatchService.requestOTP(payload);
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message || 'Failed to request OTP');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to request OTP');
    }
  }
);

export const verifyOTP = createAsyncThunk(
  'dispatch/verifyOTP',
  async (payload: VerifyOTPRequest, { rejectWithValue }) => {
    try {
      const response = await dispatchService.verifyOTP(payload);
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message || 'Failed to verify OTP');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to verify OTP');
    }
  }
);

export const getItemByCode = createAsyncThunk(
  'dispatch/getItemByCode',
  async (code: string, { rejectWithValue }) => {
    try {
      const response = await dispatchService.getItemByCode(code);
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message || 'Failed to get item by code');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to get item by code');
    }
  }
);

export const getDispatchLogs = createAsyncThunk(
  'dispatch/getDispatchLogs',
  async (dispatchId: string, { rejectWithValue }) => {
    try {
      const response = await dispatchService.getDispatchLogs(dispatchId);
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message || 'Failed to get dispatch logs');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to get dispatch logs');
    }
  }
);

// State interface
interface DispatchState {
  dispatches: Dispatch[];
  currentDispatch: Dispatch | null;
  scannedItem: ItemScanResult | null;
  dispatchLogs: any[];
  loading: boolean;
  error: string | null;
  otpRequested: boolean;
  otpVerified: boolean;
}

const initialState: DispatchState = {
  dispatches: [],
  currentDispatch: null,
  scannedItem: null,
  dispatchLogs: [],
  loading: false,
  error: null,
  otpRequested: false,
  otpVerified: false,
};

const dispatchSlice = createSlice({
  name: "dispatch",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentDispatch: (state) => {
      state.currentDispatch = null;
    },
    clearScannedItem: (state) => {
      state.scannedItem = null;
    },
    clearDispatchLogs: (state) => {
      state.dispatchLogs = [];
    },
    resetOTPState: (state) => {
      state.otpRequested = false;
      state.otpVerified = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Dispatch
      .addCase(createDispatch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDispatch.fulfilled, (state, action: PayloadAction<Dispatch>) => {
        state.loading = false;
        state.dispatches.push(action.payload);
        // Sort dispatches by dispatchDate descending (newest first)
        state.dispatches.sort((a, b) => new Date(b.dispatchDate).getTime() - new Date(a.dispatchDate).getTime());
        state.currentDispatch = action.payload;
      })
      .addCase(createDispatch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update Dispatch
      .addCase(updateDispatch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDispatch.fulfilled, (state, action: PayloadAction<Dispatch>) => {
        state.loading = false;
        const index = state.dispatches.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.dispatches[index] = action.payload;
        } else {
          state.dispatches.push(action.payload);
        }
        // Sort dispatches by dispatchDate descending (newest first)
        state.dispatches.sort((a, b) => new Date(b.dispatchDate).getTime() - new Date(a.dispatchDate).getTime());
        state.currentDispatch = action.payload;
      })
      .addCase(updateDispatch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get Dispatch Details
      .addCase(getDispatchDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDispatchDetails.fulfilled, (state, action: PayloadAction<Dispatch>) => {
        state.loading = false;
        state.currentDispatch = action.payload;
      })
      .addCase(getDispatchDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      //Get all dispatch
      .addCase(getAllDispatch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllDispatch.fulfilled, (state, action: PayloadAction<Dispatch[]>) => {
        state.loading = false;
        state.dispatches = action.payload;
      })
      .addCase(getAllDispatch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get Driver Dispatches
      .addCase(getDriverDispatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDriverDispatches.fulfilled, (state, action: PayloadAction<Dispatch[]>) => {
        state.loading = false;
        state.dispatches = action.payload;
      })
      .addCase(getDriverDispatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Request OTP
      .addCase(requestOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestOTP.fulfilled, (state) => {
        state.loading = false;
        state.otpRequested = true;
      })
      .addCase(requestOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state) => {
        state.loading = false;
        state.otpVerified = true;
        // Optionally update current dispatch status
        if (state.currentDispatch) {
          state.currentDispatch.deliveryOtpVerified = true;
        }
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get Item by Code
      .addCase(getItemByCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getItemByCode.fulfilled, (state, action: PayloadAction<ItemScanResult>) => {
        state.loading = false;
        state.scannedItem = action.payload;
      })
      .addCase(getItemByCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get Dispatch Logs
      .addCase(getDispatchLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDispatchLogs.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.dispatchLogs = action.payload;
      })
      .addCase(getDispatchLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearCurrentDispatch,
  clearScannedItem,
  clearDispatchLogs,
  resetOTPState,
} = dispatchSlice.actions;

export default dispatchSlice.reducer;
